import isInEvo from '../common/isInEvo';
import { getTargetOrigin } from './getTargetOrigin';

/**
 * Detect the correct Evo origin at runtime based on the current hostname.
 * Preprod environments (containing 'myschoolportal-test') use preprodgo,
 * all other environments use the production go origin.
 * This matches the pattern used in stryver-web server-side config.
 */
const EVO_ORIGIN = (function () {
	const hostname = window.location.hostname || '';
	if (hostname.indexOf('myschoolportal-test') !== -1) {
		return 'https://preprodgo.accessacloud.com';
	}
	return 'https://go.accessacloud.com';
})();

/**
 * Sends a LocationChanged postMessage to the parent window so that
 * Evo updates its own URL / tab state to reflect the navigation.
 *
 * Without this message Evo does not know the iframe content has
 * navigated and the click appears to do nothing.
 *
 * The message format and target origins match the pattern established
 * in stryver-web/_footer_foot.cfm and msp-admin EvoLocationTracker.
 *
 * @param {string} url - The URL being navigated to
 */
export function notifyEvoOfNavigation(url) {
	const message = JSON.stringify({
		type: 'LocationChanged',
		url: url,
		title: {
			featureName: document.title || 'Portal',
			instanceName: 'My School Portal',
		},
	});

	try {
		window.parent.postMessage(message, EVO_ORIGIN);
	} catch (e) {
		// Silently ignore cross-origin errors
	}
}

/**
 * Navigate to an msp-admin route.
 * Sends a postMessage to the parent window (msp-admin) so the parent
 * handles the navigation via its own router. This avoids loading the
 * entire portal inside the current iframe.
 *
 * Falls back to direct window.location.href only when the page is not
 * inside an iframe (i.e. window === window.parent).
 *
 * @param {string} route - The admin route (e.g. '/payments')
 * @param {string} [id] - Optional entity ID to append to the route
 */
export function navigateToAdminTool(route, id = '') {
	const url = id ? `/msp-admin/#${route}/${id}` : `/msp-admin/#${route}`;
	const routePath = id ? `${route}/${id}` : route;

	if (window.parent !== window) {
		try {
			window.parent.postMessage({
				type: 'navigate',
				url: url,
				route: routePath,
			}, getTargetOrigin());
			return;
		} catch (e) {
			// Fall through to direct navigation
		}
	}

	window.location.href = url;
}

/**
 * Navigate to an internal URL within the portal.
 * When in Evo (iframe), notifies Evo via postMessage then navigates in-place.
 * When not in Evo, opens in a new browser tab.
 * @param {string} url - The internal URL to navigate to
 */
export function navigateToInternalUrl(url) {
	if (isInEvo()) {
		notifyEvoOfNavigation(url);
		window.location.href = url;
	} else {
		safeWindowOpen(url, '_blank');
	}
}

/**
 * Navigate to a payment URL.
 * When in Evo (iframe), external URLs are opened in a new browser tab to
 * prevent the Evo iframe from navigating away. Same-origin (internal) URLs
 * are navigated in-place as usual.
 * When not in Evo, always navigates in-place to preserve existing behaviour.
 * @param {string} url - The payment/redirect URL
 */
export function navigateToPaymentUrl(url) {
	if (isInEvo() && !isInternalUrl(url)) {
		safeWindowOpen(url, '_blank');
	} else {
		window.location.href = url;
	}
}

/**
 * Checks whether a URL points to the same origin as the current page.
 * @param {string} url - The URL to check
 * @returns {boolean} true if the URL is same-origin
 */
function isInternalUrl(url) {
	try {
		const parsed = new URL(url, window.location.origin);
		return parsed.origin === window.location.origin;
	} catch (e) {
		return true;
	}
}

/**
 * Returns an onClick handler for internal links when in Evo context.
 * Prevents default link behavior, notifies Evo via postMessage, then
 * navigates the iframe directly via window.location.href so the link
 * opens in the same Evo tab.
 * Returns undefined when not in Evo so the default link behavior applies.
 * @param {string} url - The internal URL to navigate to
 * @returns {Function|undefined} Click handler or undefined
 */
export function getEvoOnClickHandler(url) {
	if (!isInEvo()) return undefined;
	return (e) => {
		e.preventDefault();
		notifyEvoOfNavigation(url);
		window.location.href = url;
	};
}

/**
 * Safely opens a URL in a new window/tab with fallback handling for
 * environments where popups may be blocked (e.g. Evo iframe without
 * allow-popups sandbox attribute).
 *
 * If window.open succeeds, returns the new window reference.
 * If window.open is blocked and we are in Evo, sends an 'open-external'
 * postMessage to the Evo parent so it can open the URL on our behalf.
 * If window.open is blocked outside Evo, falls back to navigating
 * the current page.
 *
 * @param {string} url - The URL to open
 * @param {string} [target='_blank'] - The window target name
 * @param {string} [features=''] - Window features string (for popups)
 * @returns {Window|null} The opened window reference, or null
 */
export function safeWindowOpen(url, target = '_blank', features = '') {
	const newWindow = window.open(url, target, features);

	if (!newWindow || newWindow.closed) {
		if (isInEvo()) {
			try {
				window.parent.postMessage({
					type: 'open-external',
					url: url,
				}, EVO_ORIGIN);
			} catch (e) {
				// Silently ignore cross-origin errors
			}
		} else {
			window.location.href = url;
		}
		return null;
	}

	return newWindow;
}
